import { useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { AccountBannerStore } from 'account/account-banner';
import { Button as ButtonX } from 'account/recurring-payment/components/common';
import tracking from 'account/recurring-payment/lib/tracking';
import { Button } from 'components/buttons/button';
import { ButtonType } from 'components/buttons/button-types';
import { FeaturePanel } from 'components/panels/feature-panel/feature-panel';
import { AppContext } from 'core/context/app.context';
import { FeatureFlag } from 'core/enums/feature-flag';
import { SitePlatform } from 'core/enums/site-platform';
import { AnalyticsStructureStore } from 'core/stores/analytics-structure.store';
import { AmountToPayCardProps } from './amount-to-pay-card-props';
import { AmountInput } from '../components/amount-input';
import { AmountInputMessage } from '../components/amount-input-message';
import styles from '../amount-to-pay.module.scss';

export const AmountToPayStandardCard = observer<AmountToPayCardProps>((props) => {
  const { amountToPayStore, isPaymentBlocked, onPayNow, analyticsEntryPoint, onSetupRecurringPayment, onManageRecurringPayment } = props;
  const { t } = useTranslation();
  const { currencyStore, platform, featureStore, recurringPaymentStore } = useContext(AppContext);
  const bannerStore = useLocalObservable(() => new AccountBannerStore());

  const tcsApply = featureStore.hasViewPermission(FeatureFlag.AccountTsAndCsApply);

  const hasRecurringPaymentFeature = featureStore.hasViewPermission(FeatureFlag.RecurringPaymentRoute);
  const { isInitialized, hasRecurringPayment, hasConnectedService } = recurringPaymentStore;
  const showRecurringPaymentButton = hasRecurringPaymentFeature && isInitialized;
  const showSetupRecurringPaymentButton = showRecurringPaymentButton && !hasRecurringPayment;
  const showManageRecurringPaymentButton = showRecurringPaymentButton && hasRecurringPayment;
  const isRecurringPaymentButtonEnabled = hasConnectedService;

  AnalyticsStructureStore.updateProperty('editState', amountToPayStore.isEditingValue ? 'close' : 'edit');

  useEffect(() => {
    if (tcsApply) {
      bannerStore.initialize(featureStore.hasViewPermission(FeatureFlag.AccountTsAndCsBannerUseMahLink));
    }
  }, []);

  return (
    <div className={`row ${styles.cardContainer} ${styles.standard} d-flex align-items-center`}>
      <div className="col-12 col-md-7 col-lg-7 col-xl-12">
        <div className={`row ${styles.topHeading}`}>
          <h5>{t('account.amountToPay.totalAmount')}</h5>
        </div>

        <AmountInput
          id="compactAmountToPayInput"
          amountToPayStore={amountToPayStore}
          allowEdit={featureStore.hasEditPermission(FeatureFlag.AllowStandardAmountToPayEdit)}
          styleConfig={{
            buttonType: ButtonType.Primary,
            inputClass: styles.textInput,
            containerClasses: `mb-0 d-flex align-items-center ${styles.amount}`,
          }}
        />

        <AmountInputMessage amountToPayStore={amountToPayStore} currencyStore={currencyStore} />

        <FeaturePanel hasFeature={featureStore.hasViewPermission(FeatureFlag.ViewBalanceCurrencyConversion)}>
          <div
            className="mb-2 text-primary text-button mt-2 cursor-pointer"
            onClick={() => currencyStore.changeSelectedCurrency()}
          >
            <span className="cust-mr-1">{t('account.amountToPay.switch')}</span>
            <span>{currencyStore?.currency?.code}</span>
            <i className="icon-device-swap cust-mx-1" />
            <span>{currencyStore?.inactiveCurrency?.code}</span>
          </div>
        </FeaturePanel>

        <div
          className={clsx(
            'text-caption-large text-hug text-dark-60 mt-lg-2',
            platform === SitePlatform.gotv && 'text-white'
          )}
        >
          <Trans
            i18nKey="account.amountToPay.nextPayment"
            defaults="{{amountDue}} due by {{ dueDate, datetime }}"
            values={{
              dueDate: amountToPayStore?.dueDate,
              amountDue: `${amountToPayStore?.formattedValue}`,
              formatParams: {
                dueDate: { day: 'numeric', month: 'long' },
              },
            }}
            components={{ 1: <span /> }}
          />
        </div>
        <div
          className={clsx(
            'text-caption-large text-hug text-dark-60 cust-mb-2',
            platform === SitePlatform.gotv && 'text-white'
          )}
        >
          <Trans
            i18nKey="account.amountToPay.viewingDays"
            defaults="{{daysRemaining}} Viewing days left"
            values={{
              daysRemaining: amountToPayStore?.daysRemaining,
            }}
            components={{ 1: <span /> }}
          />
        </div>
        <FeaturePanel hasFeature={featureStore.hasViewPermission(FeatureFlag.ViewBalancePaymentType)}>
          <div
            className={clsx(
              'text-caption-large text-hug text-dark-60 cust-mb-2',
              platform === SitePlatform.gotv && 'text-white'
            )}
          >
            <Trans
              i18nKey="account.amountToPay.paymentMethod"
              defaults="<1>Payment Method:</1> {{paymentMethod}}"
              values={{
                paymentMethod: amountToPayStore?.paymentMethod,
              }}
              components={{ 1: <strong /> }}
            />
          </div>
        </FeaturePanel>
      </div>
      <div className={`${styles.buttonContainer} col-12 col-md-5 col-lg-5 col-xl-12 d-flex justify-content-center justify-content-md-end justify-content-xl-center flex-column`}>
        {isPaymentBlocked ? (
          <div className="text-small">{t('common.paymentBlocked')}</div>
        ) : (
          <Button
            meta={{
              type: platform === SitePlatform.dstv ? ButtonType.Primary : ButtonType.Light,
              title: t('buttons.payNow'),
              name: 'payNowButton',
              areaName: 'Self Service Accounts - My account dashboard',
              actionName: 'payNow',
              componentName: 'amountToPayCard',
              disabled: amountToPayStore.disablePayButton,
              entryPoint: analyticsEntryPoint
            }}
            classes={['align-self-center', 'full-width', `${styles.wideButton}`]}
            onClick={onPayNow}
            analyticsComponentName="editableAmountToPayButton"
          />
        )}
        {showSetupRecurringPaymentButton &&
          <ButtonX
            meta={{
              ...tracking.amountToPay.setup,
              type: ButtonType.OutlineDark,
              title: t('account.amountToPay.setupRecurringPayment'),
              disabled: !isRecurringPaymentButtonEnabled,
            }}
            classes={['align-self-center', 'full-width', `${styles.wideButton}`, 'mt-3']}
            onClick={onSetupRecurringPayment}
          />}
        {showManageRecurringPaymentButton &&
          <ButtonX
            meta={{
              ...tracking.amountToPay.manage,
              type: ButtonType.OutlineDark,
              title: t('account.amountToPay.manageRecurringPayment'),
              disabled: !isRecurringPaymentButtonEnabled,
            }}
            classes={['align-self-center', 'full-width', `${styles.wideButton}`, 'mt-3']}
            onClick={onManageRecurringPayment}
          />}
      </div>
      {tcsApply && (
        <div className="d-flex justify-content-center cust-pt-2">
          <a
            className="text-link-underline text-small-bold"
            href={bannerStore.bannerLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('signedOut.tcApply')}
          </a>
        </div>
      )}
    </div>
  );
});